import { forwardRef, type InputHTMLAttributes, type ReactNode, useEffect, useImperativeHandle, useRef } from "react";

export type OtsCheckboxSize = "compact" | "default";

export type OtsCheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> & {
  /** Shown beside the box; leave out for a bare box named by `aria-label`, as in a grid row. */
  label?: ReactNode;
  description?: ReactNode;
  /** Some, not all, of what it stands for is ticked, e.g. a driver with part of their tickets selected. */
  indeterminate?: boolean;
  size?: OtsCheckboxSize;
  /** Classes for the wrapping label when there is one. */
  containerClassName?: string;
};

const boxClasses: Record<OtsCheckboxSize, string> = {
  compact: "size-3.5",
  default: "size-4",
};

/**
 * Foundational themed checkbox. A native input underneath, so clicks, Shift-click, Space, and forms behave as
 * the browser does; `indeterminate` shows a partly-ticked state.
 */
export const OtsCheckbox = forwardRef<HTMLInputElement, OtsCheckboxProps>(function OtsCheckbox(
  { label, description, indeterminate = false, size = "default", className, containerClassName, disabled, ...props },
  ref,
) {
  const input = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => input.current as HTMLInputElement);
  // `indeterminate` is a property, not an attribute.
  useEffect(() => {
    if (input.current) input.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const box = (
    <input
      {...props}
      ref={input}
      type="checkbox"
      disabled={disabled}
      aria-checked={indeterminate ? "mixed" : undefined}
      className={[
        boxClasses[size],
        "shrink-0 cursor-pointer rounded accent-[var(--ots-primary)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      ].filter(Boolean).join(" ")}/>
  );
  if (label == null) return box;

  return (
    <label className={[
      "flex cursor-pointer items-start gap-2",
      size === "compact" ? "text-xs" : "text-sm",
      disabled ? "cursor-not-allowed opacity-60" : "",
      containerClassName,
    ].filter(Boolean).join(" ")}>
      <span className={["flex items-center", size === "compact" ? "h-4" : "h-5"].join(" ")}>{box}</span>
      <span className="min-w-0">
        <span className="block truncate text-[var(--ots-text)]">{label}</span>
        {description != null && <span className="block text-[var(--ots-text-muted)]">{description}</span>}
      </span>
    </label>
  );
});
